// «Με την υπογραφή μου» (owner requirement 16, 22 Oct 2026): every composer that can print the advisor's signature
// asks per document, OFF by default. The last choice is remembered per document type and per user on this device
// (a convenience only — it never turns on when the user has no signature image of their own). The image shown and
// printed is always the current user's own (domain: usableSignature); another team member's never appears.
import { useState } from 'react';
import { usableSignature } from '@vero/domain';
import { useRuntime, type Runtime } from './runtime.tsx';

export type SignDoc = 'letter' | 'share_pdf' | 'proposal' | 'report' | 'summary' | 'meeting' | 'payslip';
const key = (userId: string | null | undefined, doc: SignDoc) => `avrastro.signature.${userId || 'local'}.${doc}`;

/** the remembered choice for this document type (false unless the user opted in before AND owns a signature image) */
export function rememberedSignature(rt: Pick<Runtime, 'view' | 'userId'>, doc: SignDoc): boolean {
  if (!usableSignature(rt.view, rt.userId)) return false;
  try { return globalThis.localStorage?.getItem(key(rt.userId, doc)) === '1'; } catch { return false; }
}
function remember(userId: string | null | undefined, doc: SignDoc, on: boolean) {
  try { globalThis.localStorage?.setItem(key(userId, doc), on ? '1' : '0'); } catch { /* private window: not remembered */ }
}

/** [chosen, set] — `chosen` is only ever true while the user has their own signature image */
export function useSignatureChoice(doc: SignDoc): [boolean, (on: boolean) => void] {
  const rt = useRuntime();
  const [on, setOn] = useState(() => rememberedSignature(rt, doc));
  const own = !!usableSignature(rt.view, rt.userId);
  return [on && own, (x: boolean) => { setOn(x); remember(rt.userId, doc, x); }];
}

/** The checkbox. Without an own signature image it is disabled and says where to add one. */
export function SignatureOptIn({ doc, value, onChange }: { doc: SignDoc; value: boolean; onChange: (on: boolean) => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const own = usableSignature(rt.view, rt.userId);
  return (
    <div className="signature-optin" data-testid={`sign-optin-${doc}`}>
      <label className="check" title={t('branding.signature_note')}>
        <input type="checkbox" checked={value && !!own} disabled={!own} onChange={(e: { target: HTMLInputElement }) => onChange(e.target.checked)} data-testid={`sign-optin-${doc}-check`} />
        {t('sign_optin.label')}
      </label>
      {own && value && <img src={own} alt={t('branding.signature_alt')} className="signature-optin-img" style={{ maxHeight: 28, maxWidth: 120, objectFit: 'contain', verticalAlign: 'middle' }} />}
      {!own && <small className="muted tiny">{t('sign_optin.none')}</small>}
    </div>
  );
}
